import * as React from 'react'
import { ArrowRight } from '@phosphor-icons/react'

const DeleteIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.1035 3.39014C12.7204 3.39024 13.2215 3.89046 13.2217 4.50732V5.89404H15.0264C15.4126 5.89433 15.7254 6.20795 15.7256 6.59424C15.7256 6.98066 15.4127 7.29415 15.0264 7.29443H14.2393L13.5518 15.4526C13.5031 16.0313 13.0192 16.477 12.4385 16.4771H7.59668C7.01589 16.4771 6.53113 16.0314 6.48242 15.4526L5.7959 7.29443H5.00879C4.62219 7.29443 4.30859 6.98084 4.30859 6.59424C4.30875 6.20777 4.62229 5.89404 5.00879 5.89404H6.8125V4.50732C6.81271 3.89052 7.31288 3.39035 7.92969 3.39014H12.1035ZM7.85645 15.0767H12.1787L12.834 7.29443H7.20117L7.85645 15.0767ZM8.21289 5.89404H11.8213V4.79053H8.21289V5.89404Z"
      fill="#E42309"
    />
  </svg>
)
import { RatingScale } from './types'
import { DescriptionTextarea } from './description-textarea'
import { ScaleBadge } from './scale-badge'

type EditableScaleCardProps = {
  scale: RatingScale
  scaleIndex: number
  errors?: {
    label?: string
    description?: string
  }
  onChange: (value: Partial<RatingScale>) => void
  onRemove: () => void
}

export const EditableScaleCard: React.FC<EditableScaleCardProps> = ({
  scale,
  scaleIndex,
  errors,
  onChange,
  onRemove,
}) => {
  const inputClass = (hasError?: unknown) =>
    `h-[52px] w-full rounded-lg px-4 text-[16px] font-normal leading-7 text-[#1D264A] placeholder:text-[#97A0B8] focus:outline-none ${
      hasError
        ? 'border border-red-500 bg-[#EEF1FA]'
        : 'border border-transparent bg-[#EEF1FA]'
    }`

  return (
    <div className="rounded-[16px] border border-[#D9DDE7] bg-white px-7 py-7 shadow-[0_8px_24px_rgba(29,38,74,0.08)]">
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="mb-0 text-[16px] font-medium leading-[20px] text-[#1D264A]">
          Scale {scaleIndex}
        </p>

        {scale.kind === 'mapped' ? (
          <div className="flex items-center gap-3">
            <ScaleBadge
              label={scale.baseLabel || 'Current Label'}
              color={scale.color}
            />
            <ArrowRight size={18} className="text-[#5E6580]" />
            <ScaleBadge
              label={scale.label || 'New Label'}
              color={scale.color}
              faded={!scale.label}
            />
          </div>
        ) : (
          <ScaleBadge
            label={scale.label || 'Label'}
            color={scale.color}
            faded={!scale.label}
          />
        )}
      </div>

      {scale.kind === 'mapped' ? (
        <>
          <div className="mb-6">
            <label className="mb-2 block text-[15px] font-normal leading-6 text-[#5E6580]">
              Current Label
            </label>

            <input
              type="text"
              readOnly
              value={scale.baseLabel || ''}
              className="h-[52px] w-full rounded-lg border border-transparent bg-[#D9DEEB] px-4 text-[16px] font-normal leading-7 text-[#5E6580] focus:outline-none"
            />
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-[15px] font-normal leading-6 text-[#5E6580]">
              New Label
            </label>

            <input
              type="text"
              value={scale.label}
              onChange={(event) => onChange({ label: event.target.value })}
              placeholder="New Label"
              className={inputClass(!!errors?.label)}
            />

            {errors?.label && (
              <p className="mt-2 mb-0 text-sm text-red-600">{errors.label}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-[15px] font-normal leading-6 text-[#5E6580]">
              New Description
            </label>

            <DescriptionTextarea
              value={scale.description}
              onChange={(value) => onChange({ description: value })}
              error={errors?.description}
            />
          </div>
        </>
      ) : (
        <>
          <div className="mb-6">
            <label className="mb-2 block text-[16px] font-normal leading-6 text-[#5E6580]">
              Label
            </label>

            <input
              type="text"
              value={scale.label}
              onChange={(event) => onChange({ label: event.target.value })}
              placeholder="Label"
              className={inputClass(!!errors?.label)}
            />

            {errors?.label && (
              <p className="mt-2 mb-0 text-sm text-red-600">{errors.label}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-[16px] font-normal leading-6 text-[#5E6580]">
              Description
            </label>

            <DescriptionTextarea
              value={scale.description}
              onChange={(value) => onChange({ description: value })}
              error={errors?.description}
            />
          </div>
        </>
      )}

      {scale.kind === 'extra' && (
        <button
          type="button"
          onClick={onRemove}
          className="font-talent inline-flex items-center gap-2 text-[16px] font-medium leading-[20px] text-[#E42309] hover:text-[#cc1f08]"
        >
          <DeleteIcon />
          Remove
        </button>
      )}
    </div>
  )
}
